import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { XMarkIcon, UserGroupIcon, BellIcon } from '@heroicons/react/24/outline'
import { useAuth } from '@/contexts/AuthContext'
import { Transition } from '@headlessui/react'
import { usePendingAffiliations } from '@/hooks/usePendingAffiliations'

interface AffiliationRequestNotificationProps {
  pendingCount?: number
}

// sessionStorage key for the dismiss latch. AuthContext scrubs this on
// login + logout so a fresh login always re-shows the prompt. Single
// key (no hotelId suffix) — there's only one active notification per
// session; it aggregates every active hotel seat.
export const AFFILIATION_NOTIF_DISMISS_KEY = 'affiliation-notif-dismissed'

/**
 * Floating notification component that shows when there are new pending affiliation requests.
 * Positioned in the bottom-right corner of the screen.
 */
export function AffiliationRequestNotification({ pendingCount: initialCount }: AffiliationRequestNotificationProps) {
  const { t } = useTranslation(['dashboard', 'common'])
  const { user } = useAuth()
  const navigate = useNavigate()
  const [isVisible, setIsVisible] = useState(false)
  // Persist dismissal across navigations + reloads within the same browser
  // session. sessionStorage auto-clears on tab close; AuthContext scrubs
  // this key on login/logout so a fresh login always re-shows the prompt.
  const [isDismissed, setIsDismissed] = useState(() => {
    try { return sessionStorage.getItem(AFFILIATION_NOTIF_DISMISS_KEY) === '1' } catch { return false }
  })

  // Check if user has hotel access — ACTIVE seats only (a revoked grant
  // must not keep the prompt alive).
  const isHotelOwner = user?.userType === 'hotel_admin'

  // Pending requests aggregated over every active hotel seat (PUX-05 —
  // watching hotels[0] missed requests on seats 2..n and 403'd the whole
  // count when the first grant was inactive).
  const { pendingAffiliations, activeHotelIds } = usePendingAffiliations(!isDismissed)
  const hasHotelAccess = isHotelOwner || activeHotelIds.length > 0

  // Calculate pending count from API or use initial count
  const pendingCount = pendingAffiliations?.length ?? initialCount ?? 0

  // Show notification when there are pending requests
  useEffect(() => {
    if (hasHotelAccess && !isDismissed && pendingCount > 0) {
      // Show notification after a short delay
      const timer = setTimeout(() => {
        setIsVisible(true)
      }, 2000)

      return () => clearTimeout(timer)
    }
  }, [hasHotelAccess, isDismissed, pendingCount])

  const handleDismiss = () => {
    setIsVisible(false)
    setIsDismissed(true)
    try { sessionStorage.setItem(AFFILIATION_NOTIF_DISMISS_KEY, '1') } catch { /* storage disabled, fall back to in-memory */ }
  }

  // Don't render if user doesn't have hotel access or no pending requests
  if (!hasHotelAccess || pendingCount === 0 || isDismissed) {
    return null
  }

  return (
    <Transition
      show={isVisible}
      enter="transition ease-out duration-300 transform"
      enterFrom="translate-x-full opacity-0"
      enterTo="translate-x-0 opacity-100"
      leave="transition ease-in duration-200 transform"
      leaveFrom="translate-x-0 opacity-100"
      leaveTo="translate-x-full opacity-0"
    >
      {/* On narrow mobile viewports the original `max-w-sm` (24rem ≈ 384px)
          plus `bottom-6 end-6` (no opposite-side anchor) lets the card
          extend past the viewport on the start side, dragging the page
          into horizontal scroll. Pin both sides with `start-6 end-6` on
          mobile so the card shrinks to fit, and restore the auto width +
          24rem cap from `sm:` upward. */}
      <div className="fixed bottom-6 start-6 end-6 sm:start-auto z-50 sm:max-w-sm">
        <div className="bg-white rounded-xl shadow-2xl border border-neutral-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#000056] to-[#9328FF] px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-white/20 rounded-lg">
                <BellIcon className="h-5 w-5 text-white" />
              </div>
              <span className="text-white font-medium text-sm">
                {t('dashboard:newAffiliationRequests', 'New Requests')}
              </span>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              aria-label={t('common:close')}
            >
              <XMarkIcon className="h-5 w-5 text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-amber-100 rounded-full">
                <UserGroupIcon className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-neutral-900">
                  {pendingCount} {pendingCount === 1
                    ? t('dashboard:pendingAffiliationRequest', 'pending request')
                    : t('dashboard:pendingAffiliationRequests', 'pending requests')}
                </p>
                <p className="text-xs text-neutral-500">
                  {t('dashboard:reviewAndApprove', 'Review and approve new affiliates')}
                </p>
              </div>
            </div>

            {/* Action Button.
                Was a <Link to="/hotel-affiliations/pending"> with
                onClick={handleDismiss}. The dismiss handler flips
                isDismissed=true, which makes the component return
                null on the next render — and that render fired
                before react-router's Link finished navigating, so
                the click only closed the toast. Navigate
                imperatively first, then dismiss. */}
            <button
              type="button"
              onClick={() => {
                navigate('/hotel-affiliations/pending')
                handleDismiss()
              }}
              className="block w-full text-center px-4 py-2.5 bg-gradient-to-r from-[#F92769] to-[#9328FF] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity"
            >
              {t('dashboard:viewPendingRequests', 'View Pending Requests')}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  )
}

export default AffiliationRequestNotification
